import React from 'react'
import {Link} from 'react-router-dom'
import {HStack, Box, Flex, Text, useMultiStyleConfig, TextProps, FlexProps, StackProps} from '@chakra-ui/react'
import {useAnalytics} from '_customHooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {AnalyticsData} from '_types'
import {getLabel, getHierarchy} from '_analytics/utils/analyticsUtils'
import {Icon} from '_uiComponents'

interface MenuItemProps {
    href: string
    variant?: 'header' | 'title' | 'navigation' | 'ideaSuggestion' | 'genderSuggestion'
    colorMode?: 'dark' | 'light'
    children: React.ReactNode
    analyticsEventData?: AnalyticsData
    renderaslink?: boolean
    arrow?: 'none' | 'left' | 'right'
    mouseEnterHandler?: () => void
    onClick?: () => void
    closeMenu?: () => void
    tabIndex?: number
    ariaLabel?: string
    hasPadding?: boolean
    hasInlineBlock?: boolean
}

/**
 * @name MenuItem
 *
 * @component
 *
 * @description
 * Renders a styled link using Flex and Text components.
 *
 * @param {MenuItemProps} props - The properties object.
 * @param {string} props.href - The destination URL for the link.
 * @param {string} [props.variant='navigation'] - The variant to be used for styling the link.
 * @param {string} [props.colorMode='dark'] - The color mode for the link styles.
 * @param {boolean} [props.renderaslink=true] - Decides to render the component as link or not.
 * @param {Function} [props.mouseEnterHandler] - Handler for mouse enter event.
 * @param {Function} [props.onClick] - Handler for onClick event.
 * @param {Function} [props.closeMenu] - Handler for close menu event.
 * @param {string} [props.arrow='none'] - the arrow to be displayed ('none', 'left', 'right').
 * @param {AnalyticsData} [props.analyticsEventData] - Data for analytics event tracking.
 * @param {number} [props.tabIndex=0] - The tab index for the link.
 * @param {string} [props.ariaLabel] - The aria-label for accessibility.
 * @param {boolean} [props.hasPadding=true] - Whether to apply padding styles.
 * @param {boolean} [props.hasInlineBlock=false] - Whether to apply inline-block display style.
 * @param {React.ReactNode} props.children - The content to be displayed inside the link.
 *
 * @returns {JSX.Element} The rendered MenuItem component.
 */
const MenuItem: React.FC<MenuItemProps> = ({
    href,
    variant = 'navigation',
    colorMode = 'dark',
    children,
    renderaslink = true,
    arrow = 'none',
    analyticsEventData,
    mouseEnterHandler,
    closeMenu,
    onClick,
    tabIndex = 0,
    ariaLabel,
    hasPadding = true,
    hasInlineBlock = false,
}) => {
    const styles = useMultiStyleConfig('MenuItem', {variant, colorMode, hasPadding, hasInlineBlock}) as Record<string, FlexProps & StackProps & TextProps>
    const {analyticsEventMenuClick} = useAnalytics()
    const {locale} = useMultiSite()
    const isExternalUrl = href.startsWith('https://')

    const analyticsHandler = () => {
        if (analyticsEventData) {
            analyticsEventMenuClick({
                action: analyticsEventData?.mainMenu || '',
                label: getLabel(analyticsEventData),
                hierarchy: getHierarchy(analyticsEventData),
                menuTemplate: analyticsEventData?.analyticsTemplate || '',
            })
        }
    }

    const clickHandler = () => {
        if (onClick) {
            onClick()
        } else {
            setTimeout(() => {
                analyticsHandler()
            }, 0)
        }

        closeMenu?.()
    }

    return (
        <Flex
            as={renderaslink ? (isExternalUrl ? 'a' : Link) : 'button'}
            {...styles.linkWrapper}
            {...(isExternalUrl ? {href} : {to: `/${locale.alias}${href}`})}
            onClick={clickHandler}
            onMouseEnter={mouseEnterHandler}
            data-testid={`navigation-${variant === 'title' ? 'title' : 'link'}`}
            tabIndex={tabIndex}
            aria-label={ariaLabel}
        >
            {arrow === 'none' && <Text {...styles.linkText}>{children}</Text>}

            {arrow === 'left' && (
                <>
                    <HStack {...styles.mobileWrapper}>
                        <Box {...styles.arrowContainer} {...styles.arrowLeft}>
                            <Icon name="IconChevronLeft" size="xs" />
                        </Box>
                        <Text {...styles.linkText} {...styles.centerText}>
                            {children}
                        </Text>
                    </HStack>
                </>
            )}

            {arrow === 'right' && (
                <>
                    <HStack {...styles.mobileWrapper}>
                        <Text {...styles.linkText} {...styles.leftText}>
                            {children}
                        </Text>
                        <Box {...styles.arrowContainer} {...styles.arrowRight}>
                            <Icon name="IconChevronRight" size="xs" />
                        </Box>
                    </HStack>
                </>
            )}
        </Flex>
    )
}

export {MenuItem}
